import {
    AddAdUnitBuilderAssetAction,
    AppState,
    AssetFormSelectionResult,
    assetFormSelector,
    BuildAdUnit,
    RemoveAdUnitBuilderAssetAction,
    existingAdUnitListSelector
} from "@store";
import * as cn from "classnames";
import { FetchingState } from "rd-redux-http";
import * as React from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import "./AdUnitBuilder.css";
import { AssetEditForm } from "./AssetEditForm";
import { LoadingSpinner } from "../LoadingSpinner";

interface AdUnitBuilderComponentProps {
    assets?: AssetFormSelectionResult;
    isLoading: boolean;
    isImageLoading: boolean;
    error?: string;
    canConfigureNewAdunit: boolean;
    buildStage: string;
    onBuildAdUnit: () => void;
    onAddAsset: (assetPropery: string) => void;
    onRemoveAsset: (assetProperty?: string, index?: number) => void;
}

interface AdUnitBuilderComponentState {
    loadingStage: string;
}

class AdUnitBuilderComponent extends React.Component<AdUnitBuilderComponentProps, AdUnitBuilderComponentState> {
    constructor(props: AdUnitBuilderComponentProps) {
        super(props);
        this.state = {
            loadingStage: ""
        };
    }

    componentDidUpdate() {
        const { buildStage } = this.props;

        if (buildStage === "" || this.state.loadingStage === buildStage) {
            return;
        }

        return this.setState({
            loadingStage: buildStage
        });
    }

    render() {
        const {
            assets,
            error,
            isLoading,
            isImageLoading,
            onAddAsset,
            onRemoveAsset,
            onBuildAdUnit,
            canConfigureNewAdunit
        } = this.props;

        const { loadingStage } = this.state;

        if (!assets) {
            return null;
        }

        return (
            <div className="AdUnitBuilder">
                <AssetEditForm
                    assetPropertyName="main"
                    className="AdUnitBuilder__form main"
                    canConfigureNewAdunit={canConfigureNewAdunit}
                />
                {assets.nested.map(a => (
                    <AdUnitBuilderFormContainer
                        title={a.title}
                        className="AdUnitBuilder__nested-forms"
                        onAdd={a.isCollection ? () => onAddAsset(a.name) : undefined}
                        key={a.name}
                        maxLength={a.max}
                        currentLength={a.data.length}
                        canConfigureNewAdunit={canConfigureNewAdunit}
                    >
                        {a.data.map((_, index, data) => (
                            <AssetEditForm
                                assetPropertyName={a.name}
                                index={index}
                                onRemove={a.isCollection ? () => onRemoveAsset(a.name, index) : undefined}
                                className="AdUnitBuilder__form nested"
                                key={`${index}`}
                                min={a.min}
                                currentLength={data.length}
                                canConfigureNewAdunit={canConfigureNewAdunit}
                            />
                        ))}
                    </AdUnitBuilderFormContainer>
                ))}

                {isLoading && (
                    <div className="AdUnitBuilder__loader">
                        <div className="AdUnitBuilder__loader-inner">
                            <LoadingSpinner className="AdUnitBuilder__loader-spinner" />
                        </div>
                    </div>
                )}
                <div className="AdUnitBuilder__header">
                    <button
                        className={cn(
                            "common-btn AdUnitBuilder_custom-btn",
                            canConfigureNewAdunit ? "" : "AdUnitBuilder_custom-btn-disabled"
                        )}
                        onClick={onBuildAdUnit}
                        type="button"
                        disabled={!!isImageLoading || !canConfigureNewAdunit}
                    >
                        Build Ad Unit
                    </button>
                    <span className="AdUnitBuilder__stage">{loadingStage === "Finished" ? "" : loadingStage}</span>
                </div>

                {!!error && <div className="AdUnitBuilder__error">{error}</div>}
            </div>
        );
    }
}

function AdUnitBuilderFormContainer({
    children,
    title,
    className,
    onAdd,
    maxLength,
    currentLength,
    canConfigureNewAdunit
}: AdUnitBuilderFormContainerProps) {
    return (
        <div className={cn("AdUnitBuilderFormContainer", className)}>
            <div className="AdUnitBuilderFormContainer__head">
                {title && <div className="AdUnitBuilderFormContainer__title">{title}</div>}
            </div>
            <div className="AdUnitBuilderFormContainer__inner-wrapper">
                <div
                    className={cn(
                        "AdUnitBuilderFormContainer__content",
                        onAdd ? "AdUnitBuilderFormContainer__content_border" : ""
                    )}
                >
                    {children}
                </div>
                {onAdd && (
                    <button
                        className={cn(
                            "AdUnitBuilderFormContainer__add-item common-btn important",
                            currentLength >= maxLength || !canConfigureNewAdunit
                                ? "AdUnitBuilderFormContainer__add-item_disabled"
                                : ""
                        )}
                        disabled={!canConfigureNewAdunit}
                        onClick={onAdd}
                    >
                        + Add New
                    </button>
                )}
            </div>
        </div>
    );
}

interface AdUnitBuilderFormContainerProps {
    title?: string;
    children: React.ReactChild | React.ReactChild[];
    className?: string;
    maxLength?: number;
    currentLength?: number;
    canConfigureNewAdunit: boolean;
    onAdd?: () => void;
}

export const AdUnitBuilder = connect(
    (appState: AppState): Partial<AdUnitBuilderComponentProps> => {
        const adUnitBuildingState = appState.adUnitBuilder.adUnitBuilding;
        const assets = assetFormSelector(appState);

        const isLoading = adUnitBuildingState.fetchState === FetchingState.FETCHING;

        const error =
            adUnitBuildingState.fetchState !== FetchingState.INITIAL &&
            adUnitBuildingState.fetchState === FetchingState.ERROR
                ? adUnitBuildingState.error.errorType === "response"
                    ? adUnitBuildingState.error.error.error
                    : "An error occurred while building your ad unit. Please try again later. If the error persist, reach out to us for support."
                : undefined;

        const adUnits = existingAdUnitListSelector(appState);

        const selectedAdUnit = adUnits.find(
            el => el.id === appState.campaignState.campaignCreativesForm.fields.adUnitId
        );

        const canConfigureNewAdunit: boolean =
            (!appState.campaignState.campaignCreativesForm.fields.adUnitId &&
                appState.adUnitBuilder.assetForms.main &&
                appState.adUnitBuilder.assetForms.main.fields.name) ||
            (selectedAdUnit &&
                appState.adUnitBuilder.assetForms.main &&
                selectedAdUnit.name === appState.adUnitBuilder.assetForms.main.fields.name)
                ? true
                : false;

        const buildStage = FetchingState.isSuccess(appState.adUnitBuilder.adUnitById)
            ? appState.adUnitBuilder.adUnitById.data.buildStage
            : "";

        return {
            assets,
            error,
            isLoading,
            canConfigureNewAdunit,
            buildStage
        };
    },
    (dispatch: Dispatch): Partial<AdUnitBuilderComponentProps> => ({
        onAddAsset(assetPropery: string) {
            dispatch(AddAdUnitBuilderAssetAction({ assetPropery }));
        },
        onRemoveAsset(assetPropery: string, index: number) {
            dispatch(RemoveAdUnitBuilderAssetAction({ assetPropery, index }));
        },
        onBuildAdUnit() {
            dispatch(BuildAdUnit.trigger({}));
        }
    })
)(AdUnitBuilderComponent);
